import { useState } from 'react';

import { validYMD } from '@/components/forms/utils/dateValidationRules';
import {
  convertToYMD,
  convertToDate,
  getTodayDateStr
} from '@/components/forms/utils/dateConversionRules';

import { WidgetWriterProps } from '../widget.type';
import { isLockedWidget } from '@/components/forms/utils/links';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';
import { Calendar } from '@/shadcn/calendar';
import { SelectSingleEventHandler } from 'react-day-picker';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/shadcn/button';
import { FakeInput, UnstyledInput } from '@/shadcn/fakeInput';
import { TestId } from '@/types/testId';

type Props = {
  todayVisible?: boolean;
} & WidgetWriterProps;

export const DateEntry = ({
  required,
  readOnly = false,
  todayVisible = true,
  id,
  name,
  onChange,
  value = '',
  label,
  invalid,
  formContext,
  disabled
}: Props) => {
  // parse the date and check for errors
  const error = !!value && !validYMD(value);

  // check if the widget is locked because of forms links
  const isLocked = isLockedWidget(id, formContext?.links);
  const isDisabled = (readOnly || isLocked || disabled) as boolean;

  // open or close calendar
  const [activeCalendar, setActiveCalendar] = useState(false);

  const onDatePickerChange: SelectSingleEventHandler = newDateStr => {
    onChange(convertToYMD(newDateStr));
    return setActiveCalendar(false);
  };

  // reset dates to todays day
  const setToday = () => {
    if (isDisabled) return;
    onChange(getTodayDateStr());
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // we prevent empty strings from being set as the value and instead set it to undefined
    onChange(e.target.value ? e.target.value : undefined);
  };

  return (
    <div className="flex items-center gap-2">
      <FakeInput
        invalid={error || invalid}
        className="flex max-w-[150px] items-center justify-between"
        disabled={isDisabled}
      >
        <UnstyledInput
          id={id}
          type="date"
          value={value}
          onChange={onInputChange}
          readOnly={isDisabled}
          onClick={e => e.preventDefault()}
          disabled={isDisabled}
          required={required}
          data-cy={`alt-date-${name}`}
          className="no-calendar relative -mr-4 w-fit px-0 pl-2 uppercase"
          data-testid={TestId.FormWriter.DateEntry.DateInput}
          aria-label={label}
          aria-invalid={error || invalid}
        />
        {!isLocked && (
          <>
            <Popover open={activeCalendar} onOpenChange={setActiveCalendar}>
              <PopoverTrigger asChild>
                <Button
                  size="icon"
                  onClick={() => setActiveCalendar(!activeCalendar)}
                  variant="ghost"
                  className="hover:text-primary text-muted-foreground z-2 mr-1"
                  data-testid={TestId.FormWriter.DateEntry.CalendarButton}
                  aria-label="Click to open the calendar"
                  data-cy={`open-calendar-button-${name}`}
                  disabled={isDisabled}
                >
                  <CalendarIcon />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-fit p-0">
                <Calendar
                  mode="single"
                  selected={value && !error ? convertToDate(value) : undefined}
                  onSelect={onDatePickerChange}
                  disabled={isDisabled}
                  required={required}
                  initialFocus
                  data-testid={TestId.FormWriter.DateEntry.Calendar}
                  data-cy={`calendar-${name}`}
                />
              </PopoverContent>
            </Popover>
          </>
        )}
      </FakeInput>

      {!isDisabled && todayVisible && (
        <Button
          variant="ghost"
          type="button"
          onClick={setToday}
          disabled={isDisabled}
          className="text-primary! text-sm font-normal normal-case"
          data-testid={TestId.FormWriter.DateEntry.TodayButton}
          aria-label="Click to set the date to today"
          data-cy={`set-today-button-${name}`}
        >
          Today
        </Button>
      )}
    </div>
  );
};
